import { Injectable } from '@angular/core';

import { environment as buildEnv } from '@env/environment';
import { joinApiBaseUrl } from '@core/http/url.util';

import type { Environment } from './environment.model';
import type { RuntimeConfig } from './runtime-config.model';

const API_PREFIX_PATTERN = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isWsUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'ws:' || parsed.protocol === 'wss:';
  } catch {
    return false;
  }
}

function normalizePrefix(value: string): string {
  return value.replace(/^\/+|\/+$/g, '');
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  private runtime: RuntimeConfig | null = null;

  /** Build-time snapshot (replaced per configuration). */
  get build(): Environment {
    return buildEnv;
  }

  get production(): boolean {
    return buildEnv.production;
  }

  get name(): Environment['name'] {
    return buildEnv.name;
  }

  get runtimeConfigUrl(): string {
    return buildEnv.runtimeConfigUrl;
  }

  get logging(): Environment['logging'] {
    return buildEnv.logging;
  }

  get auth(): Environment['auth'] {
    return buildEnv.auth;
  }

  /** Effective API origin after runtime overrides. */
  get apiUrl(): string {
    return this.runtime?.apiUrl ?? buildEnv.apiUrl;
  }

  /** Effective API prefix after runtime overrides. */
  get apiPrefix(): string {
    return this.runtime?.apiPrefix ?? buildEnv.apiPrefix;
  }

  get salePresentationUrl(): string {
    return this.runtime?.salePresentationUrl ?? buildEnv.salePresentationUrl;
  }

  get wsEndpoint(): string {
    return this.runtime?.wsEndpoint ?? buildEnv.wsEndpoint;
  }

  get assetsS3Url(): string {
    return this.runtime?.assetsS3Url ?? buildEnv.assetsS3Url;
  }

  /** Full REST base: `apiUrl` + `apiPrefix` (e.g. `https://host/api/v1`). */
  get apiBaseUrl(): string {
    return joinApiBaseUrl(this.apiUrl, this.apiPrefix);
  }

  mergeRuntime(config: RuntimeConfig): void {
    this.runtime = {
      ...this.runtime,
      ...this.validateRuntimeConfig(config),
    };
  }

  /**
   * Loads optional runtime JSON before the app shell renders.
   * Uses `fetch` so it does not depend on HttpClient (avoids interceptor loops).
   */
  async loadRuntimeConfig(): Promise<void> {
    const url = this.runtimeConfigUrl;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) {
        console.warn(`[Remmi] Runtime config ${url} returned ${res.status}; using build-time config.`);
        return;
      }
      const data = (await res.json()) as RuntimeConfig;
      this.mergeRuntime(data);
    } catch (error) {
      console.warn(`[Remmi] Runtime config ${url} could not be loaded; using build-time config.`, error);
    }
  }

  private validateRuntimeConfig(config: RuntimeConfig): RuntimeConfig {
    const safe: RuntimeConfig = {};
    if (typeof config.apiUrl === 'string' && isHttpUrl(config.apiUrl)) {
      safe.apiUrl = config.apiUrl.replace(/\/+$/, '');
    }
    if (typeof config.apiPrefix === 'string') {
      const normalized = normalizePrefix(config.apiPrefix);
      if (
        (normalized === '' || API_PREFIX_PATTERN.test(normalized)) &&
        !normalized.includes('..')
      ) {
        safe.apiPrefix = normalized;
      }
    }
    if (typeof config.salePresentationUrl === 'string' && isHttpUrl(config.salePresentationUrl)) {
      safe.salePresentationUrl = config.salePresentationUrl.replace(/\/+$/, '');
    }
    if (typeof config.wsEndpoint === 'string' && isWsUrl(config.wsEndpoint)) {
      safe.wsEndpoint = config.wsEndpoint;
    }
    if (typeof config.assetsS3Url === 'string' && isHttpUrl(config.assetsS3Url)) {
      safe.assetsS3Url = config.assetsS3Url.replace(/\/+$/, '');
    }
    return safe;
  }
}
